// src/components/blocks/DeckBox.jsx
import React, { useContext, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { DeckContext } from '../../context/DeckContextV3';
import EdgeIndicator from './edgeIndicatorV3/EdgeIndicatorV3';
import BaseBlock from './BaseBlockV3';
import { formatFeetInches } from '../../utils/measurementUtils';
import EdgeLabel from '../../../../../../enums/edgeLabel';
import { DeckBoxProps } from '../../../../../../types/shared/propsV3';
import { useDesignSpanContext } from '../../context/DesignSpanContext';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';

const DeckBox: React.FC<DeckBoxProps> = ({
	label,
	block,
	onSelect,
	onUpdateBlock,
	setIsDragging,
}) => {
	const { state, dispatch } = useContext(DeckContext);
	const { reface } = useQuoteState();
	const { scaleFactor } = state;
	const { maxSpanIn } = useDesignSpanContext();

	const nodeRef = useRef<HTMLDivElement>(null); // Create a ref for the DOM node

	// Determine if DeckBox should be frozen
	const isFrozen =
		state.selectedBlock &&
		(state.selectedBlock.type === 'Bumpout' ||
			state.selectedBlock.type === 'Stair' ||
			state.selectedBlock.type === 'Landing');

	// Define DeckBox dimensions
	const deckBoxWidth = block.displayDimensions.width;
	const deckBoxHeight = block.displayDimensions.height;

	useEffect(() => {
		let dropbeamsNeeded;

		if (maxSpanIn) {
			if (block.dimensions.depth > maxSpanIn && !reface) {
				dropbeamsNeeded = Math.ceil(block.dimensions.depth / maxSpanIn - 1);
			} else {
				dropbeamsNeeded = 0;
			}
		}

		if (dropbeamsNeeded == undefined) return;

		if (dropbeamsNeeded && !block.dropbeam?.isIncluded) {
			dispatch({
				type: 'SET_DROPBEAM_NEEDED',
				payload: {
					show: true,
					block: block,
					dropbeamsNeeded,
				},
			});
		} else if (!dropbeamsNeeded && block.dropbeam?.isIncluded) {
			dispatch({
				type: 'SET_DROPBEAM_NOT_NEEDED',
				payload: {
					show: true,
					block: block,
				},
			});
		}
	}, [block.dimensions.depth, maxSpanIn, reface]);

	return (
		<BaseBlock
			block={block}
			onSelect={onSelect}
			onUpdateBlock={onUpdateBlock}
			setIsDragging={setIsDragging}
			disabled={isFrozen} // Disable DeckBox dragging if a Bumpout or Stair is selected
			nodeRef={nodeRef} // Pass nodeRef to BaseBlock
		>
			<Box
				ref={nodeRef} // Attach nodeRef to the DOM node
				style={{ position: 'absolute' }}
				sx={{
					transform: `rotate(${block.rotation}deg)`,
					width: deckBoxWidth * scaleFactor, // e.g., 100px
					height: deckBoxHeight * scaleFactor, // e.g., 100px
					bgcolor: '#8BC34A', // Green background
					cursor: 'pointer',
					position: 'absolute',
					zIndex: 10,
					border: '0.5px solid rgba(255, 255, 255, 0.1)',
					overflow: 'visible', // Allow Stairs and Bumpouts to be visible outside
				}}
				onClick={(e) => {
					e.stopPropagation();
					onSelect(block);
				}}
				onTouchStart={(e) => {
					e.stopPropagation();
				}}
				onTouchEnd={(e) => {
					e.stopPropagation();
					e.preventDefault();
					onSelect(block);
				}}
				data-test={`deckbox-${block.id}`}
			>
				<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
					{/* Edge Indicators */}
					{block.edges.map((edge) => (
						<EdgeIndicator
							key={edge.label}
							edge={edge}
							onSelectEdge={(label: EdgeLabel) => {
								dispatch({
									type: 'SELECT_BLOCK',
									payload: block,
								});

								dispatch({
									type: 'SELECT_EDGE',
									payload: { blockId: block.id, edgeLabel: label },
								});
							}}
							isSelected={
								state.selectedEdge?.blockId == block.id &&
								state.selectedEdge?.edgeLabel === edge.label
							}
							parentIsSelected={state.selectedBlock?.id === block.id}
						/>
					))}

					<Box
						position="absolute"
						left={0}
						top={0}
						display={'flex'}
						justifyContent={'center'}
						alignItems={'center'}
						width={'100%'}
						height={'100%'}
						onClick={(e) => {
							e.stopPropagation();
							onSelect(block);
						}}
						onTouchStart={(e) => {
							e.stopPropagation();
						}}
						onTouchEnd={(e) => {
							e.stopPropagation();
							e.preventDefault();
							onSelect(block);
						}}
						sx={{ cursor: 'pointer' }}
					>
						<Typography
							variant="body1"
							lineHeight={1.15}
							color="rgba(0, 0, 0, 0.6)"
							noWrap
							zIndex={10}
						>
							<p>
								<b>{label}</b>
							</p>
							{formatFeetInches(block.dimensions.depth)}
							<span className="px-0.5">&times;</span>
							{formatFeetInches(block.dimensions.width)}
							<span className="px-0.5">&times;</span>
							{formatFeetInches(block.dimensions.height)}
						</Typography>
					</Box>
				</Box>
			</Box>
		</BaseBlock>
	);
};

export default DeckBox;
